import { ArrowLeft, Mail } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { useMember, useMemberActivity } from '@/lib/api-hooks';
import { cn } from '@/lib/utils';

export function MemberDetailPage() {
  const { id } = useParams();
  const { data: member, isLoading } = useMember(id);
  const { data: activity } = useMemberActivity(id);
  const items = activity?.items ?? [];

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading member…</p>;
  }

  if (!member) {
    return (
      <div className="flex flex-col items-start gap-4">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to team
        </Link>
        <p className="text-muted-foreground text-sm">Member not found.</p>
      </div>
    );
  }

  const label = member.name ?? member.email;

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/settings"
        className="inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to team
      </Link>

      <div className="flex items-start gap-4">
        <span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-semibold text-lg">
          {member.picture ? (
            <img src={member.picture} alt="" className="h-full w-full object-cover" />
          ) : (
            label.slice(0, 1).toUpperCase()
          )}
        </span>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-semibold text-2xl tracking-tight">{label}</h1>
            <Badge variant="outline" className="capitalize">
              {member.role}
            </Badge>
            {member.status !== 'active' && (
              <Badge
                variant="outline"
                className={cn(
                  'border-transparent capitalize',
                  member.status === 'banned'
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {member.status}
              </Badge>
            )}
          </div>
          <a
            href={`mailto:${member.email}`}
            className="inline-flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground"
          >
            <Mail className="size-3.5" />
            {member.email}
          </a>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="space-y-1">
          <h2 className="font-semibold text-lg tracking-tight">Activity</h2>
          <p className="text-muted-foreground text-sm">What this member has done.</p>
        </div>
        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm">No activity recorded yet.</p>
        ) : (
          <div className="flex flex-col">
            {items.map((item) => (
              <div key={item.id} className="border-b py-3 last:border-0">
                <p className="font-medium text-sm">{item.title}</p>
                <p className="text-muted-foreground text-sm">{item.body}</p>
                <p className="mt-0.5 text-muted-foreground text-xs">{item.date}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
